import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { makeStyles } from "@material-ui/core/styles";
import SpeedDial from "@material-ui/lab/SpeedDial";
import SpeedDialIcon from "@material-ui/lab/SpeedDialIcon";
import SpeedDialAction from "@material-ui/lab/SpeedDialAction";
import Fab from "@material-ui/core/Fab";
import AddLocationIcon from "@material-ui/icons/AddLocation";
import AddPersonIcon from "@material-ui/icons/PersonAdd";
import FilterIcon from "@material-ui/icons/FilterListRounded";
import TrafficIcon from "@material-ui/icons/Traffic";
import Supercluster from "supercluster";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Drawer from "@material-ui/core/Drawer";
import isEqual from "lodash.isequal";
import queryString from "query-string";

// Import traffic layer component
import TrafficLayer from "../../components/MapLayers/TrafficLayer";

import PropertyMarker from "../../components/Markers/Property";
import ClusterMarker from "../../components/Markers/Cluster";
import PropertyDialog from "../../components/Dialogs/Property";
import CreatePropertyDialog from "../../components/Dialogs/CreatePropertyStepper";
import UserMarker from "../../components/Markers/User";
import UserDialog from "../../components/Dialogs/User";
import ResponseMessaging from "../../components/ResponseMessaging";
import CreateUserDialog from "../../components/Dialogs/CreateUserStepper";
import MapFilterDialog from "../../components/Dialogs/MapFilter";
import ErrorDialog from "../../components/Dialogs/Error";
import { isUserRole } from "../../utils/user";
import { initialState } from "../../store/reducers/mapFilter";
import * as actions from "../../store/actions";
import { setUsers } from "../../store/features/users";
import getMapProperties from "../../store/selectors/getMapProperties";
import * as colors from "../../styles/colors";
import isCardinalActive from "../../utils/isCardinalActive";
import {
  geoJsonFormat,
  getTotalPoints,
  getAllLeaves,
  isLocationInBounds,
} from "../../utils/map";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  map: {
    height: "calc(100vh - 128px)",
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative", // Allow absolute positioning relative to map container
  },
  drawer: {
    width: 360,
    flexShrink: 0,
  },
  drawerPaper: {
    width: 360,
  },
  toolbar: theme.mixins.toolbar,
  filterFab: {
    margin: theme.spacing(1),
    position: "absolute",
    zIndex: theme.zIndex.appBar - 1,
  },
  filterIcon: {
    marginRight: theme.spacing(1),
  },
  mapControls: {
    position: "absolute",
    bottom: 64,
    right: 32,
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    zIndex: theme.zIndex.appBar - 1,
    pointerEvents: "none",
    "& > *": {
      pointerEvents: "auto",
    },
  },
  speedDial: {
    margin: 0,
    position: 'static',
  },
  tooltipTitle: {
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
}));

const createMapOptions = (maps) => {
  return {
    panControl: true,
    mapTypeControl: false,
    scrollwheel: true,
    zoomControl: false,
    fullscreenControl: false,
  };
};

const supercluster = new Supercluster({ radius: 100, nodeSize: 64 });

const MapContent = ({
  properties,
  mapProperties,
  match,
  history,
  users,
  user,
  location,
  dispatch,
  mapFilter,
}) => {
  const classes = useStyles();
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(true);
  const [clustersState, setClustersState] = useState({
    map: null,
    clusters: [],
    properties: {
      byId: {},
      ids: [],
    },
    totalPoints: 0,
  });
  const googleMap = useRef(null);
  
  // Traffic layer state
  const [trafficEnabled, setTrafficEnabled] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(updateTimesStrings, 60000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);
  const getClusters = (map) => {
    if (mapProperties.ids.length === 0 || !map) return;
    try {
      const clusters = supercluster.getClusters(
        [
          map.marginBounds.sw.lng,
          map.marginBounds.sw.lat,
          map.marginBounds.ne.lng,
          map.marginBounds.ne.lat,
        ],
        map.zoom
      );
      const totalPoints = getTotalPoints(clusters);
      setClustersState({
        map,
        clusters,
        properties: mapProperties,
        totalPoints,
      });
    } catch (e) {
      // do nothing
    }
  };
  useEffect(() => {
    const loadSupercluster = () => {
      supercluster.load(
        mapProperties.ids.map((propertyId) => {
          const property = mapProperties.byId[propertyId];
          return geoJsonFormat(
            property.place.coordinates.lat,
            property.place.coordinates.lng,
            property.propertyId
          );
        })
      );
    };
    loadSupercluster();
    getClusters(clustersState.map);
  }, [mapProperties]);
  useEffect(() => {
    const parsed = queryString.parse(location.search);
    if (properties[parsed.property] && !properties[parsed.property].isDeleted) {
      setCamera({
        center: properties[parsed.property].place.coordinates,
        zoom: 16,
      });
    } else if (users[parsed.user] && !users[parsed.user].status !== "OFFLINE") {
      setCamera({ center: users[parsed.user].coordinates, zoom: 16 });
    }
  }, [location.search]);

  const updateTimesStrings = () => {
    dispatch(actions.properties.setProperties(Object.values(properties)));
    dispatch(setUsers({ users: Object.values(users) }));
  };

  const onPropertyClick = (propertyId) => {
    setIsSpeedDialOpen(false);
    history.push(`/map/properties/${propertyId}`);
  };

  const onClusterClick = (clusterId, center) => {
    const zoom = supercluster.getClusterExpansionZoom(clusterId);
    setCamera({ center, zoom });
  };

  const renderUserMarkers = () => {
    if (clustersState.map === null) return;
    return Object.values(users)
      .filter((user) => {
        return (
          user.status !== "OFFLINE" &&
          moment().diff(moment(user.updatedAt), "hours") < 1 &&
          isLocationInBounds(user.location, clustersState.map.marginBounds)
        );
      })
      .map((user) => {
        return (
          <UserMarker
            key={user.userId}
            lat={user.coordinates.lat}
            lng={user.coordinates.lng}
            status={user.status}
            displayName={user.displayName}
            color={
              user.userId === queryString.parse(location.search).user
                ? colors.markers.user.SELECTED
                : user.style.color
            }
            onClick={() => history.push(`/map/users/${user.userId}`)}
            timeString={user.timeString}
          />
        );
      });
  };

  const defaultCenter = useMemo(() => getMapDefaultCenter(properties), []);
  const propertyMarkers = useMemo(
    () =>
      renderPropertyMarkers({
        clustersState,
        onClusterClick,
        onPropertyClick,
        location,
      }),
    [clustersState]
  );


  const setCamera = ({ center, zoom }) => {
    if (googleMap.current) {
      googleMap.current.setCenter(center);
      googleMap.current.setZoom(zoom);
    }
  };

  return (
    <div className={classes.root}>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={
          match.path === "/map" && queryString.parse(location.search).response
        }
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.toolbar} />
        <ResponseMessaging
          instanceId={queryString.parse(location.search).response}
        />
      </Drawer>
      {/* Bottom-right Speed Dial */}
      <div className={classes.mapControls}>
      {isUserRole({ admin: true }) && (  
        <SpeedDial
          className={classes.speedDial}
          icon={<SpeedDialIcon />}
          ariaLabel="Speed Dial"
          onClick={() => setIsSpeedDialOpen(!isSpeedDialOpen)}
          onClose={() => setIsSpeedDialOpen(false)}
          open={isSpeedDialOpen}
        >
            <SpeedDialAction
              key={"createProperty"}
              icon={<AddLocationIcon />}
              tooltipTitle={
                <span className={classes.tooltipTitle}>Create Property</span>
              }
              tooltipOpen
              onClick={() => {
                history.push("/map/properties/create");
                setIsSpeedDialOpen(false);
              }}
            />
            <SpeedDialAction
              key={"createUser"}
              icon={<AddPersonIcon />}
              tooltipTitle={
                <span className={classes.tooltipTitle}>Create User</span>
              }
              tooltipOpen
              onClick={() => {
                history.push("/map/users/create");
                setIsSpeedDialOpen(false);
              }}
            />
            <SpeedDialAction
            key={"toggleTraffic"}
            icon={<TrafficIcon />}
            tooltipTitle={
              <span className={classes.tooltipTitle}>
                {trafficEnabled ? "Hide Traffic" : "Show Traffic"}
              </span>
            }
            tooltipOpen
            onClick={() => {
              setTrafficEnabled(!trafficEnabled);
              setIsSpeedDialOpen(false);
            }}
          />
        </SpeedDial>
      )}
      </div>

      <div className={classes.map}>
        {/* Original Filter Button */}
        <Fab
          variant="extended"
          aria-label=""
          className={classes.filterFab}
          style={{
            ...(!isEqual(mapFilter, initialState)
              ? {
                  backgroundColor: colors.KONTRA_BLUE,
                  color: "white",
                }
              : null)
          }}
          onClick={() => history.push("/map/filter")}
        >
          <FilterIcon className={classes.filterIcon} />
          Filter
        </Fab>

        <GoogleMapReact
          bootstrapURLKeys={{
            key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
          }}
          options={createMapOptions}
          defaultCenter={defaultCenter}
          defaultZoom={14}
          onChange={(map) => getClusters(map)}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => {
            googleMap.current = map;
            // Store maps reference globally for layers
            window.googleMaps = maps;
          }}
        >
          {/* Traffic Layer */}
          <TrafficLayer 
            map={googleMap.current} 
            maps={window.googleMaps} 
            enabled={trafficEnabled} 
          />
          
          {propertyMarkers}
          {renderUserMarkers()}
        </GoogleMapReact>
      </div>
      {match.path === "/map/properties/:propertyId" &&
        (properties[match.params.propertyId] !== undefined ? (
          <PropertyDialog
            isOpen={match.path === "/map/properties/:propertyId"}
            onClose={() => history.push("/map")}
            property={properties[match.params.propertyId]}
          />
        ) : (
          <ErrorDialog
            open={match.path === "/map/properties/:propertyId"}
            onClose={() => history.push("/map")}
          />
        ))}
      {match.path === "/map/users/:userId" && (
        <UserDialog
          isOpen={match.path === "/map/users/:userId"}
          onClose={() => history.push("/map")}
          user={users[match.params.userId]}
        />
      )}
      {match.path === "/map/properties/create" && (
        <CreatePropertyDialog
          isOpen={match.path === "/map/properties/create"}
          onClose={() => history.push("/map")}
          serviceProviderId={user.attributes["custom:service_provider_id"]}
        />
      )}
      {match.path === "/map/users/create" && (
        <CreateUserDialog
          isOpen={match.path === "/map/users/create"}
          onClose={() => history.push("/map")}
          serviceProviderId={user.attributes["custom:service_provider_id"]}
        />
      )}
      {match.path === "/map/filter" && (
        <MapFilterDialog
          isOpen={match.path === "/map/filter"}
          onClose={() => history.push("/map")}
          filter={mapFilter}
          onSave={(filter) => dispatch(actions.mapFilter.setFilter(filter))}
        />
      )}
    </div>
  );
};

const getMapDefaultCenter = (propertiesById) => {
  return Object.values(propertiesById).length > 0
    ? Object.values(propertiesById).filter((property) => !property.isDeleted)[0]
        .place.coordinates
    : { lat: 35.1083, lng: -106.547834 };
};

const renderPropertyMarkers = ({
  clustersState,
  onClusterClick,
  onPropertyClick,
  location,
}) => {
  const { clusters, properties, totalPoints } = clustersState;
  const markers = [];
  if (totalPoints <= 75) {
    getAllLeaves(clusters, supercluster, (id) => {
      const property = properties.byId[id];
      if (property !== undefined) {
        markers.push(
          <PropertyMarker
            key={id}
            lat={property.place.coordinates.lat}
            lng={property.place.coordinates.lng}
            property={property}
            onClick={() => onPropertyClick(property.propertyId)}
            selected={id === queryString.parse(location.search).property}
          />
        );
      }
    });
  } else {
    for (const cluster of clusters) {
      if (cluster.properties) {
        const leaves = supercluster.getLeaves(
          cluster.properties.cluster_id,
          "Infinity"
        );
        const { color, fillPercentage, cardinal } = getClusterStyle({
          leaves,
          properties,
        });
        markers.push(
          <ClusterMarker
            key={cluster.id}
            lat={cluster.geometry.coordinates[1]}
            lng={cluster.geometry.coordinates[0]}
            color={color}
            count={
              cluster.properties
                ? cluster.properties.point_count_abbreviated
                : ""
            }
            fillPercentage={fillPercentage}
            cardinal={cardinal}
            onClick={() =>
              onClusterClick(cluster.id, {
                lat: cluster.geometry.coordinates[1],
                lng: cluster.geometry.coordinates[0],
              })
            }
          />
        );
      } else {
        const property = properties.byId[cluster.id];
        if (property !== undefined) {
          markers.push(
            <PropertyMarker
              key={cluster.id}
              lat={property.place.coordinates.lat}
              lng={property.place.coordinates.lng}
              property={property}
              onClick={() => onPropertyClick(property.propertyId)}
              selected={
                cluster.id === queryString.parse(location.search).property
              }
            />
          );
        }
      }
    }
  }
  return markers;
};

const getClusterStyle = ({ leaves, properties }) => {
  let color = colors.markers.property.NO_SERVICE;
  let fillPercentage = 100;
  let cardinal = false;
  for (const leaf of leaves) {
    const property = properties.byId[leaf.id];
    if (property !== undefined) {
      switch (property.markerParams.color) {
        case colors.markers.property.LOW_PRIORITY:
          if (
            color === colors.markers.property.NO_SERVICE ||
            color === colors.markers.property.LOW_PRIORITY
          ) {
            color = property.markerParams.color;
          }
          break;
        case colors.markers.property.MEDIUM_PRIORITY:
          if (
            color !== colors.markers.property.MEDIUM_PRIORITY &&
            color !== colors.markers.property.HIGH_PRIORITY
          ) {
            color = property.markerParams.color;
          }
          break;
        case colors.markers.property.HIGH_PRIORITY:
          color = property.markerParams.color;
          break;
      }
      if (
        property.markerParams.patrols &&
        property.markerParams.patrols.timeRemainingPercentage < fillPercentage
      ) {
        fillPercentage = property.markerParams.patrols.timeRemainingPercentage;
      }
      if (isCardinalActive(property.cardinal)) {
        cardinal = true;
      }
    }
  }
  return { color, fillPercentage, cardinal };
};

const mapStateToProps = (state) => ({
  properties: state.properties.byId,
  mapProperties: getMapProperties(state),
  users: state.users.byId,
  divisions: state.divisions.byId,
  filter: state.properties.filter,
  user: state.auth.user,
  mapFilter: state.mapFilter,
});

export default withRouter(connect(mapStateToProps)(MapContent));
